import React, { useState, useRef, useEffect, useContext } from 'react'
import axios from 'axios'
import CodeMirror from '@uiw/react-codemirror'
import { python } from '@codemirror/lang-python'
import { indentUnit } from '@codemirror/language'
import { FileText, Cat, Check, X, Play, Loader2 } from 'lucide-react'
import Markdown from './Markdown'
import CosmoPanel from './CosmoPanel'
import TaskObjectives from './TaskObjectives'
import { objectivesForLine } from '../content/docObjectives'
import PhaseBanner from './PhaseBanner'
import { StudyTelemetryContext } from '../telemetry/StudyTelemetryContext'
import { StudyRegionProvider, useStudyRegion } from '../telemetry/StudyRegionContext'
import { useStudyMouseEvents } from '../telemetry/StudyMouseEvents'
import { useSelectionTelemetry } from '../telemetry/useSelectionTelemetry'
import { useStudyPanelResize } from '../telemetry/useStudyPanelResize'
import { describeDocChange } from '../telemetry/codeEvents'
import { REGIONS, TASK_EVENTS, CODE_EVENTS, TERMINAL_EVENTS } from '../telemetry/telemetryEvents'
import '../../css/chi.scss'
import './study-ide.scss'

const BASE = process.env.REACT_APP_HOST_API + '/study'
const fmt = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`

// How long the "all tests passed" state stays on screen before the task advances.
const ADVANCE_DELAY_MS = 2500

// Python tracebacks name the exception on the LAST line, not the first, so the
// workspace's first-line regex (which reads a pyodide err.message) is applied to
// the tail here. Same 'SyntaxError' / 'TypeError' / … vocabulary in the payload.
const getErrorType = (errorMessage) => {
  const lines = String(errorMessage || '').trim().split('\n').filter(l => l.trim())
  const last = lines[lines.length - 1] || ''
  const match = last.match(/(\w*(?:Error|Exception))/)
  return match ? match[1] : 'UnknownError'
}

// The /chi (Cosmos) IDE, embedded as the study task screen.
// COSMO rail item exists ONLY when aiAvailable (learning tasks) — absent otherwise.
export default function StudyIDE (props) {
  const containerRef = useRef(null)

  // The region frame has to exist before anything registers into it, so the
  // provider wraps the shell and the instrumented body lives one level down.
  return (
    <StudyRegionProvider containerRef={containerRef}>
      <StudyIDEBody {...props} containerRef={containerRef} />
    </StudyRegionProvider>
  )
}

function StudyIDEBody ({ task, aiAvailable, taskNumber, taskTotal, participantId, code, setCode, remaining, onProceed, proceedBusy, proceedLabel = 'SUBMIT', kind, phase, containerRef }) {
  const [consoleTab, setConsoleTab] = useState('terminal')
  const [output, setOutput] = useState('')
  const [running, setRunning] = useState(false)
  // null until the first run; null `tests` also means the task has no suite (warm-up).
  const [tests, setTests] = useState(null)
  const [gradeError, setGradeError] = useState(null)
  const [gradedOnce, setGradedOnce] = useState(false)
  // Set between "all tests passed" and the actual advance, so the pass is legible
  // before the screen changes under them.
  const [advancing, setAdvancing] = useState(false)
  const advanceTimer = useRef(null)
  useEffect(() => () => clearTimeout(advanceTimer.current), [])

  const { logTelemetryEvent } = useContext(StudyTelemetryContext)

  // Callback refs, not useRef: these nodes are handed to region/selection hooks that
  // bind listeners in effects, and a ref object never changes identity, so those
  // effects would not re-run if the node were ever replaced. Node-in-state does.
  const [descEl, setDescEl] = useState(null)
  const [editorEl, setEditorEl] = useState(null)
  const [consoleEl, setConsoleEl] = useState(null)

  // Global listeners: MOUSE_MOVE / MOUSE_CLICK / TAB_STATE / WINDOW_RESIZE
  useStudyMouseEvents()

  // Regions, with the workspace's priorities (nested panels outrank the description)
  useStudyRegion(REGIONS.TASK_DESCRIPTION, descEl)
  useStudyRegion(REGIONS.CODE_EDITOR, editorEl)
  useStudyRegion(REGIONS.TERMINAL, consoleEl, 10)

  // Highlight / copy in the description, the editor and the console
  useSelectionTelemetry(descEl, TASK_EVENTS.SELECT, TASK_EVENTS.COPY)
  useSelectionTelemetry(editorEl, CODE_EVENTS.SELECT, CODE_EVENTS.COPY)
  useSelectionTelemetry(consoleEl, TERMINAL_EVENTS.SELECT, TERMINAL_EVENTS.COPY)

  const bodyRef = useRef(null)
  const leftRef = useRef(null)
  const layout = useStudyPanelResize(containerRef, bodyRef, leftRef)

  // Leaving mid-task loses the timed block: the step clock runs server-side and does
  // not pause, and unflushed telemetry (5s batches) dies with the tab. The browser
  // shows its own generic wording — the string only has to be non-empty to trigger it.
  useEffect(() => {
    const onBeforeUnload = (e) => {
      e.preventDefault()
      e.returnValue = ''
      return ''
    }
    window.addEventListener('beforeunload', onBeforeUnload)
    return () => window.removeEventListener('beforeunload', onBeforeUnload)
  }, [])

  const onChange = (val, viewUpdate) => {
    setCode(val)
    if (!viewUpdate?.docChanged) return
    const { name, payload } = describeDocChange(viewUpdate, val)
    logTelemetryEvent(name, payload)
  }

  const run = async () => {
    if (running) return
    setRunning(true); setConsoleTab('tests'); setOutput('Running…')

    logTelemetryEvent(TERMINAL_EVENTS.RUN, {
      code_length: code.length,
      language: 'python',
      timestamp: Date.now()
    })

    try {
      const r = await axios.post(`${BASE}/run`, { participantId, code })
      const parts = []
      if (r.data.stdout) parts.push(r.data.stdout.replace(/\n$/, ''))
      if (r.data.stderr) parts.push(r.data.stderr.replace(/\n$/, ''))
      if (r.data.timedOut) parts.push(`\n[timed out after ${Math.round(r.data.durationMs / 1000)}s]`)
      setOutput(parts.join('\n') || 'Code executed (no output).')

      // One TERMINAL_OUTPUT per printed line, matching the workspace's compiled path.
      if (r.data.stdout) {
        for (const line of r.data.stdout.split('\n')) {
          if (line.trim()) logTelemetryEvent(TERMINAL_EVENTS.OUTPUT, { output: line })
        }
      }

      if (r.data.stderr && r.data.stderr.trim()) {
        logTelemetryEvent(TERMINAL_EVENTS.ERROR, {
          message: r.data.stderr,
          type: getErrorType(r.data.stderr)
        })
      }

      if (r.data.timedOut) {
        logTelemetryEvent(TERMINAL_EVENTS.ERROR, {
          message: `Execution time limit exceeded after ${r.data.durationMs}ms`,
          type: 'TimeoutError'
        })
      }
      setTests(r.data.tests)
      setGradeError(r.data.gradeError || null)
      setGradedOnce(true)
      if (r.data.tests) {
        logTelemetryEvent(TERMINAL_EVENTS.RESULT, {
          passed_count: r.data.tests.filter(t => t.passed).length,
          total_tests: r.data.tests.length,
          detailed_results: r.data.tests.map((t, i) => ({ test_case_number: i + 1, test_name: t.name, passed: t.passed }))
        })
      }
      // The gate: all tests passing is what ends the task. There is no submit button on
      // a graded task, so this and the timer are the only two ways forward.
      //
      // Advancing the instant the last test goes green makes the pass invisible — the
      // screen swaps before they can read it, and the run they just made lands as a
      // step change rather than a success. So the console says so first, then we move.
      if (r.data.allPassed) {
        setAdvancing(true)
        setConsoleTab('tests')
        advanceTimer.current = setTimeout(onProceed, ADVANCE_DELAY_MS)
      }
    } catch (e) {
      const message = e.response?.data?.error || e.message
      setOutput('Runner error: ' + message)
      setTests(null)
      logTelemetryEvent(TERMINAL_EVENTS.ERROR, { message, type: 'RunnerError' })
    } finally { setRunning(false) }
  }

  const passCount = tests ? tests.filter(t => t.passed).length : 0
  // `gradeable` is only known after the first run: a task with no suite returns
  // tests: null. Assume graded until told otherwise, so no submit button flashes up.
  const gradeable = gradedOnce ? tests !== null : true
  const warn = remaining != null && remaining <= 120 && remaining > 0

  // Both panels are on screen at once now, so the rail labels what is where rather
  // than switching between them — hence no onClick and no inactive state.
  const RailItem = ({ label, Icon }) => (
    <div className="chi-rail__item is-static">
      <Icon size={20} strokeWidth={1.8} /><span>{label}</span>
    </div>
  )

  return (
    <div className="chi-root study-chi" ref={containerRef}>
      {/* Top bar */}
      <header className="chi-topbar">
        <div className="chi-topbar__back study-chi__kind">{kind}</div>
        {/* Position, not identity — the task's name is already the DESC panel heading.
            No chevron: steps are server-gated, so there is nothing to open. */}
        <div className="chi-topbar__center study-chi__pos">
          {phase && <><PhaseBanner phase={phase} compact /><span className="study-chi__sep">·</span></>}
          {taskNumber && taskTotal ? `Task ${taskNumber} of ${taskTotal}` : null}
        </div>
        <div className="chi-topbar__right">
          {remaining != null && <span className={`study-chi__timer${warn ? ' warn' : ''}`}>{fmt(remaining)}</span>}
        </div>
      </header>

      {/* Body */}
      <div className="chi-body" ref={bodyRef}>
        <section
          className="chi-desc"
          style={layout.leftWidth != null ? { width: `${layout.leftWidth}px`, flex: '0 0 auto' } : undefined}
        >
          <nav className="chi-rail">
            <RailItem label="DESC" Icon={FileText} />
            {aiAvailable && <RailItem label="COSMO" Icon={Cat} />}
          </nav>

          {/* Doc and tutor share one column instead of trading places on a tab.
              The tab version unmounted CosmoPanel on every switch, which threw away
              the conversation — chat state lives in the component, so the only way to
              keep it was to stop unmounting it. Side effect worth having: participants
              can read the task and the tutor's answer at the same time. */}
          <div className="study-chi__left" ref={leftRef}>
            <div className="chi-desc__content" ref={setDescEl}>
              <TaskObjectives domain={task.domain} />
              <Markdown text={task.descriptionMd} annotate={line => objectivesForLine(task.id, line)} />
            </div>
            {aiAvailable && (
              <>
                <div className="study-chi__chat-resizer" onMouseDown={layout.startDraggingChat} />
                <div
                  className="study-chi__cosmo"
                  style={layout.chatHeight != null ? { height: `${layout.chatHeight}px` } : undefined}
                >
                  {/* Named, not just present: "COSMO" on the rail is a product name and
                      told a first-time reader nothing about what the panel is. */}
                  <div className="study-chi__cosmo-head">AI Assistant</div>
                  <CosmoPanel participantId={participantId} code={code} />
                </div>
              </>
            )}
          </div>
        </section>

        <div className="chi-divider" onMouseDown={layout.startDraggingLeft} />

        <section className="chi-editor-side">
          <div className="chi-ed-tabs">
            <div className="chi-ed-tab">main.py</div>
            <div className="chi-ed-runtime"><span>Python 3 · Trio</span></div>
          </div>
          <div className="chi-editor" ref={setEditorEl}>
            <CodeMirror value={code} theme="dark" height="100%" extensions={[python(), indentUnit.of('    ')]} onChange={onChange} />
          </div>
          <div
            className="chi-console-resizer"
            onMouseDown={layout.startDraggingTerminal}
          />
          <div
            className="chi-console"
            ref={setConsoleEl}
            style={layout.terminalHeight != null ? { height: `${layout.terminalHeight}px` } : undefined}
          >
            <div className="chi-console__tabs">
              <div className={`chi-console__tab${consoleTab === 'tests' ? ' is-active' : ''}`} onClick={() => setConsoleTab('tests')}>
                TESTS{tests ? ` ${passCount}/${tests.length}` : ''}
              </div>
              <div className={`chi-console__tab${consoleTab === 'output' ? ' is-active' : ''}`} onClick={() => setConsoleTab('output')}>OUTPUT</div>
              <div className={`chi-console__tab${consoleTab === 'terminal' ? ' is-active' : ''}`} onClick={() => setConsoleTab('terminal')}>TERMINAL</div>
              <div className="chi-console__spacer" />
              <button className="chi-run" onClick={run} disabled={running || advancing}>
                {running ? <Loader2 size={13} className="chi-spin" /> : <Play size={13} fill="#fff" />}
                <span>{running ? 'RUNNING' : 'RUN'}</span>
              </button>
            </div>
            <div className="chi-console__body">
              {consoleTab === 'terminal' && <><b>/usercode/FILESYSTEM</b>$</>}
              {consoleTab === 'output' && <pre className="chi-output">{output || 'Press RUN to execute your code.'}</pre>}
              {consoleTab === 'tests' && (
                <div className="study-tests">
                  {advancing && (
                    <div className="study-tests__pass">
                      <Check size={15} strokeWidth={3} />
                      <span>All {tests ? tests.length : ''} tests passed — moving on…</span>
                    </div>
                  )}
                  {gradeError && <div className="study-tests__err">{gradeError}</div>}
                  {!tests && !gradeError && <div className="study-tests__empty">Press RUN to check your code against the tests.</div>}
                  {tests && tests.map(t => (
                    <div key={t.name} className={`study-tests__row${t.passed ? ' is-pass' : ''}`}>
                      <span className="study-tests__icon">
                        {t.passed ? <Check size={13} strokeWidth={3} /> : <X size={13} strokeWidth={3} />}
                      </span>
                      <span className="study-tests__name">{t.name}</span>
                      {!t.passed && t.message && <span className="study-tests__msg">{t.message}</span>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </section>
      </div>

      {/* Bottom bar */}
      <footer className="chi-bottombar">
        <div className="chi-more">
          {/* Static on graded tasks: the live count is already on the TESTS tab, and a
              bar that changes on every run competes with the console for attention. */}
          {gradeable
            ? 'The task continues when all tests pass, or when the timer ends'
            : 'Verify against the Expected Output'}
        </div>
        {/* Ungradeable tasks (the warm-up has no suite) keep a way out; graded ones are
            gated on the tests, so there is deliberately no button here. */}
        <div className="chi-bottombar__right">
          {!gradeable && (
            <button className="chi-btn chi-btn--submit" disabled={proceedBusy} onClick={onProceed}>
              {proceedBusy ? '…' : proceedLabel}
            </button>
          )}
        </div>
      </footer>
    </div>
  )
}
